import { useMemo, useState } from 'react';

import { Alert, Button, Empty, Image, Modal, Segmented, Skeleton, Tag } from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PictureOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useGetMediaOfferHistoryByArticleQuery } from 'services';
import MediaThumb from 'components/SendItems/AddMediaContent/MediaThumb';

import {
  MEDIA_TYPE_LABELS,
  MEDIA_OFFER_FIELD_LABELS,
  formatMediaOfferFieldValue,
  isPreviewableMedia,
  resolvePosterUrl,
} from './helpers';
import { splitByCommonAffix } from './historyDiff';
import DecisionMakerBadge from './DecisionMakerBadge';
import DecisionTypeBadge from './DecisionTypeBadge';
import './style.less';

/** Uzun metinlerin clamp'lenip "Devamını göster" toggle'ı alacağı karakter eşiği. */
const CLAMP_THRESHOLD = 220;

/** Değeri URL olan, diff'te tıklanabilir link olarak basılan alanlar. */
const LINK_FIELDS = ['link', 'archiveLink'];

/**
 * Diff'in tek satırda (ÖNCE → SONRA yan yana) basılabileceği toplam karakter sınırı.
 * Üstünde iki değer alt alta, kendi "Önce"/"Sonra" etiketleriyle basılır — böylece ok,
 * sarma yüzünden kendi satırında ortada asılı kalmaz. Değer modal genişliğine göre
 * seçilmiştir (geniş modalda akış kolonu ~1100px).
 */
const INLINE_DIFF_MAX = 90;

/** Sol raydaki grup etiketinde kaynak/açıklama ipucunun kırpılacağı uzunluk. */
const GROUP_HINT_MAX = 26;

/**
 * Olay tipi → ikon ve ton. Ton yalnız "nötr / bilgi" ayrımıdır; **karar sonucu renkleri
 * (yeşil/kırmızı) burada kullanılmaz** — o renkler yalnız `decision` olaylarına ve
 * `DecisionTypeBadge`'e ayrılmıştır (bkz. doküman §2.4 "UI kuralları").
 */
const ENTRY_ICON = {
  created: { Icon: PlusOutlined, tone: 'neutral' },
  field_change: { Icon: EditOutlined, tone: 'info' },
  media_added: { Icon: PictureOutlined, tone: 'info' },
  media_removed: { Icon: DeleteOutlined, tone: 'neutral' },
};

/**
 * `created` kartının altındaki "ilk değerler" paketine katlanan olay tipleri.
 *
 * Yalnız teklifin ilk **içeriği** katlanır. `decision` bilinçli olarak DIŞARIDA: yazar dışı
 * bir rol teklif oluşturduğunda BE otomatik bir `approved` kararı da yazar ve bu kayıt
 * created ile aynı versiyon + zaman damgasını taşır. Pakete katlansaydı hem varsayılan kapalı
 * bir kutunun içinde gizlenir hem de "Kararlar" filtresinden düşerdi.
 */
const CREATION_FOLDED_TYPES = ['field_change', 'media_added'];

/** Olay tipi filtresi → o filtrede gösterilecek `type` değerleri. */
const FILTER_TYPES = {
  field: ['created', 'field_change'],
  decision: ['decision'],
  media: ['media_added', 'media_removed'],
};

/** Üstteki olay tipi filtresi seçenekleri (antd Segmented). */
const TYPE_FILTER_OPTIONS = [
  { label: 'Tümü', value: 'all' },
  { label: 'Değişiklikler', value: 'field' },
  { label: 'Kararlar', value: 'decision' },
  { label: 'Medya', value: 'media' },
];

/**
 * Bir `decision` kaydındaki değişimi çözer.
 *
 * `from` dolu olsa bile karar aynı kalmış olabilir; bu durumda "eski → yeni" basmak
 * `Uygun Değildir → Uygun Değildir` gibi anlamsız bir satır üretir. Karar aynıyken not da
 * aynı olabilir (BE her yeniden-karar için yeni bir `MediaOfferDecision` satırı üretir), o
 * zaman "not güncellendi" demek de yanlış olur. Üç durum burada tek yerden ayrılır.
 *
 * Not karşılaştırmasında `null` ile `''` eşdeğer sayılır — aradaki fark kullanıcı için
 * bir değişiklik değildir.
 *
 * @param {Object} entry - `type: 'decision'` history kaydı
 * @returns {{ from?: string, to?: string, changed: boolean, noteChanged: boolean }}
 *   `changed`: iki farklı karar varsa true · `noteChanged`: değerlendirme notu farklıysa true
 */
const resolveDecisionChange = (entry) => {
  const from = entry.from?.decisionType;
  const to = entry.to?.decisionType;
  return {
    from,
    to,
    changed: Boolean(from) && Boolean(to) && from !== to,
    noteChanged: (entry.from?.decisionNote || '') !== (entry.to?.decisionNote || ''),
  };
};

/**
 * Bir grubun sol ray / çip etiketi: medya türü + ayırt edici ipucu.
 * Aynı maddede birden fazla fotoğraf grubu olabildiği için tür tek başına yetmez;
 * grubun güncel kaynağı (yoksa açıklaması) ayırt edici olarak eklenir.
 *
 * @param {Object} [group] - `getMediaOfferGroups` grubu (`mediaType` + `currentVersion`)
 * @param {string} [fallbackMediaType] - Grup listede yoksa history kaydından gelen tür
 * @returns {string}
 */
const buildGroupLabel = (group, fallbackMediaType) => {
  const type = MEDIA_TYPE_LABELS[group?.mediaType ?? fallbackMediaType] || 'Medya';
  const hint = (group?.currentVersion?.source || group?.currentVersion?.description || '').trim();
  if (!hint) return type;
  return `${type} · ${hint.length > GROUP_HINT_MAX ? `${hint.slice(0, GROUP_HINT_MAX)}…` : hint}`;
};

/**
 * Uzun metni ~3 satırda kısaltıp "Devamını göster / Gizle" ile yerinde açan metin bloğu.
 * Detay ayrı ekrana taşınmaz; kart içinde açılır. Eşik altındaki metinlerde toggle render edilmez.
 * @param {Object} props
 * @param {string} props.text - Gösterilecek metin
 */
function ClampText({ text }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = (text || '').length > CLAMP_THRESHOLD;
  return (
    <>
      <span className={`media-history-clamp${isLong && !expanded ? ' media-history-clamp--collapsed' : ''}`}>{text}</span>
      {isLong && (
        <Button
          type="link"
          size="small"
          className="media-history-clamp__toggle"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Gizle' : 'Devamını göster'}
        </Button>
      )}
    </>
  );
}

/**
 * Diff'in tek bir tarafı (önce ya da sonra). `parts` verilmişse ortak ön/son ek düz,
 * değişen orta parça vurgulu basılır — uzun URL/metinlerde farkın nerede olduğunu gözle
 * aramak gerekmesin diye. Link alanlarında değer tıklanabilir bağlantı olur.
 *
 * @param {Object} props
 * @param {string} props.field - BE alan adı (link alanı mı diye bakılır)
 * @param {string} props.text - Değerin tam görünen metni
 * @param {{ head: string, mid: string, tail: string }} [props.parts] - Ortak/değişen parçalar; yoksa düz metin
 * @param {'before'|'after'} props.variant - Görsel ton (nötr gri / vurgulu)
 */
function DiffValue({ field, text, parts, variant }) {
  const className = `media-history-diff__value media-history-diff__value--${variant}`;
  const body =
    parts && parts.mid ? (
      <>
        {parts.head}
        <mark className={`media-history-diff__mark media-history-diff__mark--${variant}`}>{parts.mid}</mark>
        {parts.tail}
      </>
    ) : (
      text
    );

  if (LINK_FIELDS.includes(field) && text) {
    return (
      <a
        className={className}
        href={text}
        target="_blank"
        rel="noreferrer noopener"
      >
        {body}
      </a>
    );
  }
  return <span className={className}>{body}</span>;
}

/**
 * `field_change` kaydının diff gösterimi.
 *
 * Renk dili bilinçli olarak **nötr**dür (ÖNCE soluk gri, SONRA vurgulu): kırmızı/yeşil bu
 * ekranda "Uygun Değildir / Uygundur" demek olduğu için eski değeri kırmızı basmak onu
 * reddedilmiş gibi okutuyordu. Ayrım renk yerine "Önce"/"Sonra" etiketleriyle verilir.
 *
 * `from` boşsa (alan ilk kez dolduruldu) yalnız yeni değer basılır.
 *
 * @param {Object} props
 * @param {Object} props.entry - `type: 'field_change'` history kaydı
 */
function FieldDiff({ entry }) {
  const beforeText = formatMediaOfferFieldValue(entry.field, entry.from);
  const afterText = formatMediaOfferFieldValue(entry.field, entry.to);

  if (beforeText === null) {
    return (
      <div className="media-history-diff">
        <DiffValue
          field={entry.field}
          text={afterText ?? '—'}
          variant="after"
        />
      </div>
    );
  }

  const segments = afterText === null ? null : splitByCommonAffix(beforeText, afterText);
  const stacked = beforeText.length + (afterText?.length ?? 0) > INLINE_DIFF_MAX;

  return (
    <div className={`media-history-diff${stacked ? ' media-history-diff--stacked' : ''}`}>
      <div className="media-history-diff__cell">
        <span className="media-history-diff__cap">Önce</span>
        <DiffValue
          field={entry.field}
          text={beforeText}
          parts={segments?.before}
          variant="before"
        />
      </div>
      {!stacked && <ArrowRightOutlined className="media-history-diff__arrow" />}
      <div className="media-history-diff__cell">
        <span className="media-history-diff__cap">Sonra</span>
        <DiffValue
          field={entry.field}
          text={afterText ?? '—'}
          parts={segments?.after}
          variant="after"
        />
      </div>
    </div>
  );
}

/**
 * Bir medya dosyasının chip gösterimi: küçük resim + dosya adı linki.
 *
 * Küçük resim `MediaThumb` ile basılır (→ `ImageWithFallback` + tür ikonu): kırık ya da
 * görsel-olmayan dosyalarda tarayıcının kırık-resim ikonu yerine tür ikonuna düşer.
 * Poster çözümü projenin tek kuralı olan `resolvePosterUrl`'den gelir; tür, history
 * kaydındaki ham `media.mediaType` değil **grubun** türüdür (kural `photo/video/audio` bekler).
 *
 * @param {Object} props
 * @param {Object} props.media - History kaydındaki Media objesi (`to` ya da `from`)
 * @param {string} [props.mediaType] - Grubun medya türü
 * @param {boolean} [props.removed=false] - Kaldırma kaydında soluk + üstü çizili gösterim
 * @param {(src: string) => void} props.onPreview - Tam-boy görüntüleme (lightbox) tetikleyici
 */
function MediaChip({ media, mediaType, removed = false, onPreview }) {
  if (!media) return null;
  const name = media.name || media.fileName || 'Medya';
  const previewable = isPreviewableMedia(media, mediaType);
  const openPreview = () => onPreview(media.fileFullUrl);
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openPreview();
  };

  return (
    <span className={`media-history-file${removed ? ' media-history-file--removed' : ''}`}>
      <span
        className={`media-history-file__thumb${previewable ? ' media-history-file__thumb--clickable' : ''}`}
        onClick={previewable ? openPreview : undefined}
        onKeyDown={previewable ? handleKeyDown : undefined}
        role={previewable ? 'button' : undefined}
        tabIndex={previewable ? 0 : undefined}
        aria-label={previewable ? `${name} — büyük görüntüle` : undefined}
      >
        <MediaThumb
          src={resolvePosterUrl(media, mediaType)}
          mediaType={mediaType}
          size={28}
          alt={name}
          bordered
        />
      </span>
      {media.fileFullUrl ? (
        <a
          href={media.fileFullUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          {name}
        </a>
      ) : (
        <span>{name}</span>
      )}
    </span>
  );
}

/**
 * `created` kaydının altındaki "ilk değerler" paketi — varsayılan **kapalı**.
 *
 * Teklifin ilk hâli 6-7 alan + dosyalar demek; bunları koşulsuz basmak tek olayı bir buçuk
 * ekran yapıp geçmişin geri kalanını görünmez kılıyordu. Kapalı özet + yerinde açılan
 * 2 sütunlu tanım ızgarası (ayrı modal değil — bkz. doküman §2.4 "UI kuralları").
 *
 * @param {Object} props
 * @param {Object[]} props.details - created ile aynı versiyon + zaman damgalı kayıtlar
 * @param {string} [props.mediaType] - Grubun medya türü (dosya chip'leri için)
 * @param {(src: string) => void} props.onPreview - Lightbox tetikleyici
 */
function CreationPackage({ details, mediaType, onPreview }) {
  const [expanded, setExpanded] = useState(false);
  const fields = details.filter((detail) => detail.type === 'field_change');
  const files = details.filter((detail) => detail.type === 'media_added');
  if (!details.length) return null;

  const summary = [fields.length ? `${fields.length} alan` : null, files.length ? `${files.length} dosya` : null].filter(Boolean).join(' · ');

  return (
    <div className="media-history-package">
      <button
        type="button"
        className="media-history-package__head"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <span>
          <strong>İlk değerler</strong>
          {summary && ` — ${summary}`}
        </span>
        <span className="media-history-package__toggle">
          {expanded ? 'Gizle' : 'Detay'}
          <DownOutlined className={`media-history-package__chevron${expanded ? ' media-history-package__chevron--open' : ''}`} />
        </span>
      </button>
      {expanded && (
        <dl className="media-history-package__body">
          {fields.map((detail) => {
            const value = formatMediaOfferFieldValue(detail.field, detail.to);
            const isLink = LINK_FIELDS.includes(detail.field);
            return (
              <div
                key={detail.__uid}
                className={`media-history-package__field${isLink ? ' media-history-package__field--wide' : ''}`}
              >
                <dt>{MEDIA_OFFER_FIELD_LABELS[detail.field] || detail.field}</dt>
                <dd>
                  {value === null ? (
                    <span className="media-history-empty">—</span>
                  ) : isLink ? (
                    <a
                      href={value}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {value}
                    </a>
                  ) : (
                    <ClampText text={value} />
                  )}
                </dd>
              </div>
            );
          })}
          {files.length > 0 && (
            <div className="media-history-package__field media-history-package__field--wide">
              <dt>Dosyalar</dt>
              <dd>
                <div className="media-history-files">
                  {files.map((detail) => (
                    <MediaChip
                      key={detail.__uid}
                      media={detail.to}
                      mediaType={mediaType}
                      onPreview={onPreview}
                    />
                  ))}
                </div>
              </dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

/**
 * Kaydın tek cümlelik özet satırı ("kim ne yaptı"). Aktör adı kayıtlarda null olabilir → "-".
 * @param {Object} props
 * @param {Object} props.entry - History kaydı
 */
function EntrySummary({ entry }) {
  const name = entry.actorUser?.fullName || '-';
  const fieldLabel = entry.field ? MEDIA_OFFER_FIELD_LABELS[entry.field] || entry.field : null;

  switch (entry.type) {
    case 'created':
      return (
        <>
          <strong>{name}</strong> medya teklifini oluşturdu
        </>
      );
    case 'field_change':
      return (
        <>
          <strong>{name}</strong>, <strong>{fieldLabel}</strong> alanını güncelledi
        </>
      );
    case 'media_added':
      return (
        <>
          <strong>{name}</strong> medya ekledi
        </>
      );
    case 'media_removed':
      return (
        <>
          <strong>{name}</strong> medya kaldırdı
        </>
      );
    case 'decision': {
      const { from, changed, noteChanged } = resolveDecisionChange(entry);
      // Karar aynı kalıp yalnız not değiştiyse "kararını değiştirdi", hiçbiri değişmediyse
      // "notunu güncelledi" yanlış olur — üç ayrı ifade.
      let action = 'karar bildirdi';
      if (from) action = changed ? 'kararını değiştirdi' : noteChanged ? 'değerlendirme notunu güncelledi' : 'kararını yineledi';
      return (
        <>
          <strong>{name}</strong> {action}
        </>
      );
    }
    default:
      return <strong>{name}</strong>;
  }
}

/**
 * Kaydın tipe göre detay gövdesi (özet satırının altında).
 * @param {Object} props
 * @param {Object} props.entry - History kaydı
 * @param {string} [props.mediaType] - Grubun medya türü
 * @param {(src: string) => void} props.onPreview - Lightbox tetikleyici
 */
function EntryDetail({ entry, mediaType, onPreview }) {
  switch (entry.type) {
    case 'field_change':
      return <FieldDiff entry={entry} />;
    case 'media_added':
    case 'media_removed': {
      const media = entry.type === 'media_added' ? entry.to : entry.from;
      return (
        <div className="media-history-files">
          <MediaChip
            media={media}
            mediaType={mediaType}
            removed={entry.type === 'media_removed'}
            onPreview={onPreview}
          />
        </div>
      );
    }
    case 'decision': {
      const { from, to, changed } = resolveDecisionChange(entry);
      const note = entry.to?.decisionNote;
      return (
        <div className="media-history-decision">
          <div className="media-history-decision__badges">
            {changed && (
              <>
                <DecisionTypeBadge text={from} />
                <ArrowRightOutlined className="media-history-diff__arrow" />
              </>
            )}
            {to && <DecisionTypeBadge text={to} />}
            {Boolean(from) && !changed && <span className="media-history-muted">karar değişmedi</span>}
          </div>
          {note && (
            <div className="media-history-note">
              <ClampText text={note} />
            </div>
          )}
        </div>
      );
    }
    default:
      return null;
  }
}

/**
 * Tek bir history kaydının akış satırı: solda olay tipi ikonu, sağda özet cümlesi,
 * meta satırı (birim rozeti · versiyon · saat · grup çipi) ve tipe göre detay gövdesi.
 *
 * Timeline noktası/çizgisi bilinçli olarak yok: tek renk kanalı hem olay tipini hem karar
 * sonucunu kodlayamıyordu. Olay tipi ikonla, karar sonucu renkle verilir.
 *
 * @param {Object} props
 * @param {Object} props.entry - History kaydı
 * @param {Object[]} [props.creationDetails=[]] - Yalnız `created` kartında: ilk değerlerin kayıtları
 * @param {string} [props.mediaType] - Grubun medya türü
 * @param {string} [props.groupLabel] - Grup çipi etiketi; verilmezse çip basılmaz (tek grup görünümü)
 * @param {string} [props.groupThumb] - Grup çipi küçük resmi
 * @param {(src: string) => void} props.onPreview - Lightbox tetikleyici
 */
function HistoryEntry({ entry, creationDetails = [], mediaType, groupLabel, groupThumb, onPreview }) {
  const decision = entry.type === 'decision' ? resolveDecisionChange(entry) : null;
  const { Icon, tone } = decision
    ? decision.to === 'not_approved'
      ? { Icon: CloseCircleOutlined, tone: 'bad' }
      : { Icon: CheckCircleOutlined, tone: 'ok' }
    : ENTRY_ICON[entry.type] || { Icon: EditOutlined, tone: 'neutral' };

  return (
    <div className="media-history-entry">
      <span className={`media-history-entry__icon media-history-entry__icon--${tone}`}>
        <Icon />
      </span>
      <div className="media-history-entry__main">
        <div className="media-history-entry__summary">
          <EntrySummary entry={entry} />
        </div>
        <div className="media-history-entry__meta">
          <DecisionMakerBadge text={entry.actorRole} />
          <Tag className="media-history-version">v{entry.version}</Tag>
          <span>{entry.occurredAt ? dayjs(entry.occurredAt).format('HH:mm') : '-'}</span>
          {groupLabel && (
            <span className="media-history-chip">
              <MediaThumb
                src={groupThumb}
                mediaType={mediaType}
                size={16}
              />
              {groupLabel}
            </span>
          )}
        </div>
        <EntryDetail
          entry={entry}
          mediaType={mediaType}
          onPreview={onPreview}
        />
        {entry.type === 'created' && (
          <CreationPackage
            details={creationDetails}
            mediaType={mediaType}
            onPreview={onPreview}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Sol ray: "Tümü" + her medya teklifi grubu. Grup ayrımı ayrı bölümler yerine filtreye
 * indirildi; böylece akış tek ve kronolojik kalır, tek bir medyaya odaklanmak tek tık olur.
 *
 * @param {Object} props
 * @param {Array<{ id: number|string, label: string, count: number, thumb?: string, mediaType?: string, decisionType?: string }>} props.items
 * @param {number} props.total - Tüm olay sayısı ("Tümü" satırı için)
 * @param {number|string} props.selected - Seçili grup id'si ya da 'all'
 * @param {(value: number|string) => void} props.onSelect
 */
function GroupRail({ items, total, selected, onSelect }) {
  return (
    <nav
      className="media-history-rail"
      aria-label="Medya teklifleri"
    >
      <div className="media-history-rail__label">Medya Teklifleri</div>
      <button
        type="button"
        className="media-history-rail__item"
        aria-pressed={selected === 'all'}
        onClick={() => onSelect('all')}
      >
        <span className="media-history-rail__thumb media-history-rail__thumb--all">
          <UnorderedListOutlined />
        </span>
        <span className="media-history-rail__text">
          <span className="media-history-rail__name">Tümü</span>
          <span className="media-history-rail__sub">
            {total} olay · {items.length} teklif
          </span>
        </span>
      </button>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className="media-history-rail__item"
          aria-pressed={selected === item.id}
          onClick={() => onSelect(item.id)}
        >
          <MediaThumb
            src={item.thumb}
            mediaType={item.mediaType}
            size={34}
            bordered
          />
          <span className="media-history-rail__text">
            <span className="media-history-rail__name">{item.label}</span>
            <span className="media-history-rail__sub">
              {item.decisionType ? <DecisionTypeBadge text={item.decisionType} /> : null}
              <span className="media-history-rail__count">{item.count}</span>
            </span>
          </span>
        </button>
      ))}
    </nav>
  );
}

/**
 * Modalın içeriği — sol ray + olay akışı. Modal `destroyOnClose` ile kapandığında bu bileşen
 * unmount olur, dolayısıyla seçili grup / filtre / açılmış paketler kendiliğinden sıfırlanır.
 *
 * @param {Object} props
 * @param {Object[]} props.entries - Madde-düzeyi history kayıtları (yeniden → eskiye)
 * @param {Array} props.groups - Makaleye ait medya teklifi grupları (tablodan gelir)
 */
function HistoryContent({ entries, groups, truncated }) {
  const [groupFilter, setGroupFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [preview, setPreview] = useState({ visible: false, src: null });

  const openPreview = (src) => setPreview({ visible: true, src });

  // Grup kimliği zenginleştirme: history kaydındaki `mediaOfferGroup` yalnız id + mediaType
  // taşır (aynı maddedeki iki fotoğraf grubu ayırt edilemez). Tablodan gelen grup listesi
  // küçük resim, kaynak ve son kararı taşıdığı için kayıtlar id üzerinden buna bağlanır.
  const groupIndex = useMemo(() => new Map((groups || []).map((group) => [group.id, group])), [groups]);

  // BE yeniden→eskiye sıralı döndürmeyi garanti eder; yine de savunmacı sıralanır.
  // Stable sort, aynı zaman damgalı oluşturma paketinin BE iç sırasını korur.
  const sorted = useMemo(
    () => [...(entries || [])].sort((a, b) => dayjs(b.occurredAt).valueOf() - dayjs(a.occurredAt).valueOf()),
    [entries]
  );

  // created gruplaması: teklifin ilk değerleri, created ile aynı version + occurredAt'e sahip
  // kayıtlardır. Madde-düzeyi akışta iki grup aynı saniyede oluşturulmuş olabileceği için
  // anahtar GRUP id'sini de içerir — aksi halde bir grubun ilk değerleri diğerinin created
  // kartının altına düşebilirdi.
  const { timelineEntries, creationDetails } = useMemo(() => {
    const keyOf = (entry) => `${entry.mediaOfferGroup?.id ?? '-'}|${entry.version}|${entry.occurredAt}`;
    const createdKeys = new Set(sorted.filter((entry) => entry.type === 'created').map(keyOf));
    const details = new Map();
    const timeline = [];

    sorted.forEach((entry) => {
      const key = keyOf(entry);
      if (CREATION_FOLDED_TYPES.includes(entry.type) && createdKeys.has(key)) {
        if (!details.has(key)) details.set(key, []);
        details.get(key).push(entry);
        return;
      }
      timeline.push(entry);
    });

    return { timelineEntries: timeline, creationDetails: { map: details, keyOf } };
  }, [sorted]);

  const railItems = useMemo(
    () =>
      (groups || []).map((group) => ({
        id: group.id,
        label: buildGroupLabel(group),
        mediaType: group.mediaType,
        thumb: resolvePosterUrl(group.currentVersion?.mediaOfferItems?.[0]?.media, group.mediaType),
        decisionType: group.currentVersion?.lastDecision?.decisionType,
        count: timelineEntries.filter((entry) => entry.mediaOfferGroup?.id === group.id).length,
      })),
    [groups, timelineEntries]
  );

  // Ray ve grup çipi yalnız gerçekten ayırt edici olduklarında gösterilir:
  //  - tek grup varsa bilgi taşımazlar,
  //  - hiçbir kayıt `mediaOfferGroup` taşımıyorsa (BE alanı döndürmezse) filtre boş sonuç
  //    üretirdi — o durumda tek düz akışa düşmek bozuk bir filtreden iyidir.
  const showRail = railItems.length > 1 && railItems.some((item) => item.count > 0);

  const visibleEntries = useMemo(
    () =>
      timelineEntries.filter(
        (entry) =>
          (groupFilter === 'all' || entry.mediaOfferGroup?.id === groupFilter) &&
          (typeFilter === 'all' || FILTER_TYPES[typeFilter].includes(entry.type))
      ),
    [timelineEntries, groupFilter, typeFilter]
  );

  // Gün ayırıcıları: akış zaten tarihe göre sıralı olduğu için ardışık aynı-gün kayıtları
  // tek başlık altında toplanır (ayrıca sort/grup gerekmez).
  const days = useMemo(() => {
    const result = [];
    visibleEntries.forEach((entry) => {
      const key = dayjs(entry.occurredAt).format('YYYY-MM-DD');
      const last = result[result.length - 1];
      if (last?.key === key) last.entries.push(entry);
      else result.push({ key, label: dayjs(entry.occurredAt).format('D MMMM YYYY'), entries: [entry] });
    });
    return result;
  }, [visibleEntries]);

  return (
    <div className={`media-history${showRail ? ' media-history--with-rail' : ''}`}>
      {showRail && (
        <GroupRail
          items={railItems}
          total={timelineEntries.length}
          selected={groupFilter}
          onSelect={setGroupFilter}
        />
      )}
      <div className="media-history-stream-wrap">
        <div className="media-history-toolbar">
          <Segmented
            size="small"
            options={TYPE_FILTER_OPTIONS}
            value={typeFilter}
            onChange={setTypeFilter}
          />
          <span className="media-history-muted">{visibleEntries.length} olay</span>
        </div>
        <div className="media-history-stream">
          {/* Güvenlik sınırına takıldıysa sessizce kırpma: kullanıcı eksik baktığını bilmeli. */}
          {truncated && (
            <Alert
              className="gx-mt-3"
              type="warning"
              showIcon
              message="Bu maddenin işlem geçmişi çok uzun; yalnızca en yeni kayıtlar gösteriliyor."
            />
          )}
          {days.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Bu filtreyle gösterilecek işlem yok."
            />
          ) : (
            days.map((day) => (
              <section key={day.key}>
                <h4 className="media-history-day">
                  <span>{day.label}</span>
                </h4>
                {day.entries.map((entry) => {
                  const group = groupIndex.get(entry.mediaOfferGroup?.id);
                  const mediaType = group?.mediaType || entry.mediaOfferGroup?.mediaType;
                  return (
                    <HistoryEntry
                      key={entry.__uid}
                      entry={entry}
                      mediaType={mediaType}
                      creationDetails={creationDetails.map.get(creationDetails.keyOf(entry)) || []}
                      groupLabel={showRail && groupFilter === 'all' ? buildGroupLabel(group, entry.mediaOfferGroup?.mediaType) : undefined}
                      groupThumb={resolvePosterUrl(group?.currentVersion?.mediaOfferItems?.[0]?.media, mediaType)}
                      onPreview={openPreview}
                    />
                  );
                })}
              </section>
            ))
          )}
        </div>
      </div>

      {/**
       * Kontrollü lightbox. Gizli tutulur (küçük resimler zaten `MediaThumb` ile çiziliyor);
       * yalnız chip tıklamasının açtığı tam-boy önizlemeyi besler. rc-image body'e portallandığı
       * için `display:none` sarmalayıcı yalnız kayıt amaçlı <img>'i gizler, önizlemeyi değil —
       * böylece modal üstüne ikinci bir modal binmez.
       */}
      {preview.src && (
        <div style={{ display: 'none' }}>
          <Image
            src={preview.src}
            preview={{
              visible: preview.visible,
              src: preview.src,
              onVisibleChange: (visible) => setPreview((state) => ({ ...state, visible })),
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Bir makale teklifindeki TÜM medya teklifi gruplarının işlem geçmişini tek modalda,
 * tek kronolojik akış olarak gösterir (tablo sağ-üstündeki "İşlem Geçmişi" butonu açar).
 *
 * Veri tek istekten gelir: `GET /api/media_offer_history/by_article/{articleOfferId}`.
 * Gruplama ve filtreleme FE'de, her kaydın taşıdığı `mediaOfferGroup` alanından yapılır;
 * BE görünürlüğü kullanıcının rolüne göre kendisi filtreler, FE ekstra filtre uygulamaz.
 *
 * Sorgu `skip: !open` ile modal açılınca çalışır; `destroyOnClose` içerik bileşenini
 * kapanışta unmount ederek filtre/seçim durumunu sıfırlar.
 *
 * @param {Object} props
 * @param {number|string} props.articleOffer - Makale teklifi id'si (geçmiş sorgusunun anahtarı)
 * @param {Array<{ id: number|string, mediaType: string, currentVersion?: Object }>} props.groups
 *   - Makaleye ait gruplar (tablodan); grup etiketi, küçük resim ve son karar buradan gelir
 * @param {boolean} props.open - Modal açık mı
 * @param {() => void} props.onClose - Modalı kapatma callback'i
 */
function MediaOfferHistory({ articleOffer, groups, open, onClose }) {
  // `isLoading` (ilk yükleme) bilinçli — `isFetching` değil: revize/karar sonrası arka plan
  // tazelemesinde içeriği skeleton'a düşürmek seçili grup/filtreyi de görsel olarak siler.
  const { data, isLoading, isError } = useGetMediaOfferHistoryByArticleQuery({ articleOffer }, { skip: !open || !articleOffer });

  return (
    <Modal
      title="İşlem Geçmişi"
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(1440px, 96vw)"
      // `centered` + viewport'a göre sabit yükseklik: geçmiş uzun bir liste olduğu için modal
      // ekranın neredeyse tamamını kullanır. Yükseklik sabit olmalı ki sol ray ve akış iki
      // bağımsız scroll alanı olsun, modal içerik uzunluğuna göre zıplamasın.
      centered
      bodyStyle={{ padding: 0, height: 'calc(100vh - 180px)' }}
      destroyOnClose
    >
      {isLoading ? (
        <div className="media-history-state">
          <Skeleton active />
        </div>
      ) : isError ? (
        <div className="media-history-state">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="İşlem geçmişi yüklenemedi."
          />
        </div>
      ) : !data?.entries?.length ? (
        <div className="media-history-state">
          <Empty description="İşlem geçmişi bulunamadı." />
        </div>
      ) : (
        <HistoryContent
          entries={data.entries}
          groups={groups}
          truncated={data.truncated}
        />
      )}
    </Modal>
  );
}

export default MediaOfferHistory;
